import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Avatar,
  Divider,
  Alert,
  Snackbar,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import ChangePasswordModal from '../../components/ChangePasswordModal/ChangePasswordModal';

const MyProfile = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
      setPhone(user.phone || '');
    }
  }, [user]);

  const handleSaveChanges = async () => {
    setLoading(true);
    // TODO: Implementar atualização de perfil by Jandev
    setTimeout(() => {
      setLoading(false);
      setSuccessMessage(t('profile.updateSuccess'));
      setShowSuccess(true);
    }, 1000);
  };

  const handlePasswordChangeSuccess = () => {
    setSuccessMessage(t('changePassword.success'));
    setShowSuccess(true);
  };

  const getUserInitials = () => {
    if (name) {
      const parts = name.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
        {t('profile.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('profile.subtitle')}
      </Typography>

      <Paper sx={{ p: 3 }}>
        {/* Profile Photo Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            {t('profile.photo')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Avatar
              sx={{
                width: 100,
                height: 100,
                fontSize: '2rem',
                fontWeight: 600,
                bgcolor: 'primary.main',
              }}
            >
              {getUserInitials()}
            </Avatar>
            <Button variant="outlined" disabled>
              {t('profile.changePhoto')}
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Profile Information */}
        <Box sx={{ mb: 4 }}>
          <TextField
            fullWidth
            label={t('profile.fullName')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={loading}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label={t('profile.email')}
            value={email}
            disabled
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label={t('profile.phone')}
            value={phone}
            disabled
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Security Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            {t('profile.security')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              backgroundColor: 'background.default',
              borderRadius: 1,
            }}
          >
            <Box>
              <Typography variant="body2" fontWeight={500}>
                {t('profile.password')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ••••••••••••
              </Typography>
            </Box>
            <Button
              variant="outlined"
              onClick={() => setOpenPasswordModal(true)}
            >
              {t('profile.changePassword')}
            </Button>
          </Box>
        </Box>

        {/* Save Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={handleSaveChanges}
            disabled={loading}
            sx={{ minWidth: 150 }}
          >
            {t('profile.saveChanges')}
          </Button>
        </Box>
      </Paper>

      {/* Change Password Modal */}
      <ChangePasswordModal
        open={openPasswordModal}
        onClose={() => setOpenPasswordModal(false)}
        onSuccess={handlePasswordChangeSuccess}
      />

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setShowSuccess(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MyProfile;
